import { createEffect, createEvent } from 'effector'
import { get, uniqBy } from 'lodash'

import { APIResult, AxiosError } from '../../core/api/Result'
import {
  GetMyInstallmentReq,
  GetMyReferralsTransactionsReq,
  GetMyTransactionsReq,
  PostInstallmentsReq,
  PostTransactionsReq,
  PutCancelInstallmentReq,
  TransactionAPI,
} from '../../services/api/Transaction'
import { $navigate } from '../History/store'
import { Routes, TransactionStatus, TransactionType } from '../../shared/enum'
import { openUrl } from '../../utils'
import { getMyFx, getStatsFx } from '../User/event'
import { getProjectsFx } from '../Project/event'

export const clearMyTransactions = createEvent()
export const clearMyReferralsTransactions = createEvent()
export const setWithdrawalImageFx = createEvent<boolean>()

export const postTransactionsFx = createEffect<PostTransactionsReq, APIResult<typeof TransactionAPI['postTransactions']>, AxiosError>(
  TransactionAPI.postTransactions,
)

export const postInstallmentsFx = createEffect<PostInstallmentsReq, APIResult<typeof TransactionAPI['postInstallments']>, AxiosError>(
  TransactionAPI.postInstallments,
)

export const getMyTransactionsFx = createEffect<GetMyTransactionsReq, APIResult<typeof TransactionAPI['getMyTransactions']>, AxiosError>(
  TransactionAPI.getMyTransactions,
)

export const getMyTransactionsNftFx = createEffect(
  TransactionAPI.getMyTransactions,
)

export const getMyTransactionsStatisticFx = createEffect(TransactionAPI.getMyTransactionsStatistic)

export const getMyPaymentFx = createEffect(async (projectId?: number[]) => {
  const trx = await TransactionAPI.getMyTransactions({
    limit: 1000,
    page: 1,
    status: [TransactionStatus.COMPLETED],
    type: [
      TransactionType.INSTALLMENT_PAYMENT,
      TransactionType.SINGLE_PAYMENT,
      TransactionType.GIFT_SHARES,
    ],
    project: projectId,
  })

  const trxInstallment = trx.data.data.items.filter(({ type }) => type === TransactionType.INSTALLMENT_PAYMENT)
  const installmentIds = uniqBy(trxInstallment, 'installment.id')
    .map((obj) => Number(get(obj, 'installment.id')))

  const installments = await Promise.all(installmentIds.map((id) => {
    return TransactionAPI.getMyInstallment({ id })
  }))

  const result = trx.data.data.items.map((trx) => {
    const installmentFind = installments.find(({ data: { data } }) => data.id === trx.installment.id)
    if (installmentFind) {
      trx.createdAt = installmentFind.data.data.createdAt
    }
    return trx
  })

  return result
})

export const getTransactionFx = createEffect(TransactionAPI.getTransaction)

export const getMyInstallmentsFx = createEffect<unknown, APIResult<typeof TransactionAPI['getMyInstallments']>, AxiosError>(
  TransactionAPI.getMyInstallments,
)

export const getMyInstallmentFx = createEffect<GetMyInstallmentReq, APIResult<typeof TransactionAPI['getMyInstallment']>, AxiosError>(
  TransactionAPI.getMyInstallment,
)
export const clearMyInstallmentFx = createEvent()

export const putCancelInstallmentFx = createEffect<PutCancelInstallmentReq, APIResult<typeof TransactionAPI['putCancelInstallment']>, AxiosError>(
  TransactionAPI.putCancelInstallment,
)

export const getMyReferralsTransactionsFx = createEffect<GetMyReferralsTransactionsReq, APIResult<typeof TransactionAPI['getMyReferralsTransactions']>, AxiosError>(
  TransactionAPI.getMyReferralsTransactions,
)

export const postPayFx = createEffect(TransactionAPI.postPay)
export const postCancelFx = createEffect(TransactionAPI.postCancel)

export const getMyTransfer = createEffect(async () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { data: { data } } = await TransactionAPI.getMyTransactions({
    limit: 3,
    page: 1,
    status: [TransactionStatus.COMPLETED],
    type: [TransactionType.TRANSFER_BALANCE],
  })
})

export const getUserInfoInstallmentsFx = createEffect(TransactionAPI.getUserInstallments)
export const getUserInfoSumInvestmentFx = createEffect(TransactionAPI.getUserSumInvestment)
export const getUserReferralTransactionFx = createEffect(TransactionAPI.getMyTransactions)
export const postTransactionNftFx = createEffect(TransactionAPI.postTransactionNft)
export const getNftFx = createEffect(TransactionAPI.getNft)
export const getNftIdFx = createEffect((id: number | string) =>
  TransactionAPI.getNft({ ids: [Number(id)] }),
)
export const getTopMyUsersFx = createEffect(TransactionAPI.getTopMyUsers)

export const getUserInfoFx = createEffect(async (userId: number) => {
  getUserInfoInstallmentsFx({ userId })
  getUserInfoSumInvestmentFx({ userId })
  getUserReferralTransactionFx({ referralId: userId, page: 1, limit: 1000 })
})

const watch = [
  postTransactionsFx,
  postInstallmentsFx,
]

watch.forEach((fx) => {
  fx.done.watch(({ result }) => {
    const { paymentInfo } = result.data.data

    if (paymentInfo?.redirectUrl) {
      openUrl(paymentInfo?.redirectUrl)
    } else {
      const navigate = $navigate.getState()
      navigate(`/${Routes.TRANSACTION}/${Routes.SUCCESS}`)

      getMyFx()
      getStatsFx(null)
      getProjectsFx(null)
    }
  })

  fx.fail.watch(() => {
    const navigate = $navigate.getState()
    navigate(`/${Routes.TRANSACTION}/${Routes.REJECT}`)
  })
})

postPayFx.done.watch(({ result }) => {
  openUrl(result.data.data.redirectUrl)
})

postCancelFx.done.watch(() => {
  const navigate = $navigate.getState()
  navigate(`/${Routes.TRANSACTION}/${Routes.REJECT}`)
  window.location.reload()
})

putCancelInstallmentFx.done.watch(() => {
  getMyInstallmentsFx(null)
  getMyFx()
})
